import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Spinner, Badge, Pagination, Tabs, Tab, Alert } from 'react-bootstrap';
import { Search, Network, MessageSquare, Plus, Edit, Trash2 } from 'lucide-react';
import api from '../../services/api';
import './Gateways.css';
const Gateways = () => {
  const [voiceGateways, setVoiceGateways] = useState([]);
  const [smsGateways, setSmsGateways] = useState([]);
  const [voicePermissions, setVoicePermissions] = useState({ vie: 0, ins: 0, upd: 0, del: 0 });
  const [smsPermissions, setSmsPermissions] = useState({ vie: 0, ins: 0, upd: 0, del: 0 });
  const [clientType, setClientType] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [smsProtocols, setSmsProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [modalType, setModalType] = useState('voice');
  const [currentVoiceGateway, setCurrentVoiceGateway] = useState({
    id_route: null, description: '', ip_number: '', tech_prefix: '', tech_prefix_out: '',
    id_prot: 1, capacity: '', capacity_term: '', max_conn_min: 100
  });
  const [currentSmsGateway, setCurrentSmsGateway] = useState({
    id: null, gw_name: '', gw_ip: '', gw_port: 2775, protocol_id: 1,
    systemid: '', systemid_term: '', tls_term_id: 0, gw_port_ssl: null,
    tcp_sess_max_in: 1, tcp_sess_num_out: 1
  });
  const [tariffDetails, setTariffDetails] = useState([]);
  const [tariffSearch, setTariffSearch] = useState('');
  const [selectedTariff, setSelectedTariff] = useState({ id: null, type: '', service: 'voice' });
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState('');
  const itemsPerPage = 30;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  useEffect(() => {
    loadGateways();
    loadProtocols();
  }, []);
  const loadGateways = async () => {
    try {
      const response = await api.get('/gateways');
      setVoiceGateways(response.data.voice.gateways);
      setSmsGateways(response.data.sms.gateways);
      setVoicePermissions(response.data.voice.permissions);
      setSmsPermissions(response.data.sms.permissions);
      setClientType(response.data.clientType);
    } catch (error) {
      console.error('Load gateways error:', error);
      setError('Failed to load gateways');
    } finally {
      setLoading(false);
    }
  };
  const loadProtocols = async () => {
    try {
      const [voiceProtoResponse, smsProtoResponse] = await Promise.all([
        api.get('/gateways/protocols'),
        api.get('/gateways/sms-protocols')
      ]);
      setProtocols(voiceProtoResponse.data);
      setSmsProtocols(smsProtoResponse.data);
    } catch (error) {
      console.error('Load protocols error:', error);
    }
  };
  const loadTariffDetails = async (tariffId, service = 'voice', page = 1, letter = '', searchTerm = '', details = false) => {
    setDetailsLoading(true);
    setCurrentPage(page);
    setSelectedLetter(letter);
    try {
      const offset = (page - 1) * itemsPerPage;
      const params = {
        search: searchTerm,
        limit: itemsPerPage,
        offset: offset,
        showDetails: details ? 'true' : 'false',
        letter: letter,
        type: service
      };
      const response = await api.get(`/tariffs/${tariffId}`, { params });
      setTariffDetails(response.data.data);
      const total = response.data.total;
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error('Load tariff details error:', error);
    } finally {
      setDetailsLoading(false);
    }
  };
  const handleSearchChange = (value) => {
    setTariffSearch(value);
    if (value.trim() !== '') {
      setSelectedLetter('');
    }
  };
  const handleSearch = () => {
    loadTariffDetails(selectedTariff.id, selectedTariff.service, 1, '', tariffSearch, showDetails);
  };
  const handleLetterClick = (letter) => {
    const newLetter = selectedLetter === letter ? '' : letter;
    setSelectedLetter(newLetter);
    loadTariffDetails(selectedTariff.id, selectedTariff.service, 1, newLetter, tariffSearch, showDetails);
  };
  const handleOffClick = () => {
    setSelectedLetter('');
    loadTariffDetails(selectedTariff.id, selectedTariff.service, 1, '', tariffSearch, showDetails);
  };
  const handlePageChange = (page) => {
    loadTariffDetails(selectedTariff.id, selectedTariff.service, page, selectedLetter, tariffSearch, showDetails);
  };
  const handleShowDetailsChange = (checked) => {
    setShowDetails(checked);
    loadTariffDetails(selectedTariff.id, selectedTariff.service, 1, selectedLetter, tariffSearch, checked);
  };
  const handleSaveVoice = async () => {
    try {
      if (editMode) {
        await api.put(`/gateways/voice/${currentVoiceGateway.id_route}`, currentVoiceGateway);
      } else {
        await api.post('/gateways/voice', currentVoiceGateway);
      }
      loadGateways();
      setShowModal(false);
      resetForm();
    } catch (error) {
      if (error.response?.status === 403) {
        setError('You do not have permission to perform this action');
      } else {
        setError('Failed to save gateway');
      }
    }
  };
  const handleSaveSms = async () => {
    try {
      if (editMode) {
        await api.put(`/gateways/sms/${currentSmsGateway.id}`, currentSmsGateway);
      } else {
        await api.post('/gateways/sms', currentSmsGateway);
      }
      loadGateways();
      setShowModal(false);
      resetForm();
    } catch (error) {
      if (error.response?.status === 403) {
        setError('You do not have permission to perform this action');
      } else {
        setError('Failed to save SMS gateway');
      }
    }
  };
  const handleDeleteVoice = async (idRoute) => {
    if (!window.confirm('Delete this gateway?')) return;
    try {
      await api.delete(`/gateways/voice/${idRoute}`);
      loadGateways();
    } catch (error) {
      if (error.response?.status === 403) {
        setError('You do not have permission to delete gateways');
      } else {
        setError('Failed to delete gateway');
      }
    }
  };
  const handleDeleteSms = async (id) => {
    if (!window.confirm('Delete this SMS gateway?')) return;
    try {
      await api.delete(`/gateways/sms/${id}`);
      loadGateways();
    } catch (error) {
      if (error.response?.status === 403) {
        setError('You do not have permission to delete SMS gateways');
      } else {
        setError('Failed to delete SMS gateway');
      }
    }
  };
  const resetForm = () => {
    setCurrentVoiceGateway({ id_route: null, description: '', ip_number: '', tech_prefix: '', tech_prefix_out: '', id_prot: 1, capacity: '', capacity_term: '', max_conn_min: 100 });
    setCurrentSmsGateway({ id: null, gw_name: '', gw_ip: '', gw_port: 2775, protocol_id: 1, systemid: '', systemid_term: '', tls_term_id: 0, gw_port_ssl: null, tcp_sess_max_in: 1, tcp_sess_num_out: 1 });
    setEditMode(false);
  };
  const handleEditVoice = (gateway) => {
    setCurrentVoiceGateway(gateway);
    setEditMode(true);
    setModalType('voice');
    setShowModal(true);
  };
  const handleEditSms = (gateway) => {
    setCurrentSmsGateway(gateway);
    setEditMode(true);
    setModalType('sms');
    setShowModal(true);
  };
  const handleAddVoice = () => {
    resetForm();
    setModalType('voice');
    setShowModal(true);
  };
  const handleAddSms = () => {
    resetForm();
    setModalType('sms');
    setShowModal(true);
  };
  const viewTariff = (tariffId, tariffType, service = 'voice') => {
    setSelectedTariff({ id: tariffId, type: tariffType, service: service });
    setTariffSearch('');
    setSelectedLetter('');
    setShowDetails(false);
    setCurrentPage(1);
    loadTariffDetails(tariffId, service, 1, '', '', false);
    setShowTariffModal(true);
  };
  const handleCloseTariffModal = () => {
    setShowTariffModal(false);
    setTariffSearch('');
    setSelectedLetter('');
    setShowDetails(false);
    setCurrentPage(1);
  };
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    let items = [];
    const maxVisible = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} />);
      items.push(<Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />);
    }
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
          {page}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      items.push(<Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />);
      items.push(<Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />);
    }
    return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
  };
  const getTableColumns = () => {
    if (showDetails) {
      return (
        <tr>
          <th>Prefix</th>
          <th>Destination</th>
          <th>Rate</th>
          <th>Currency</th>
          <th>Start Date</th>
          <th>Bill Increment</th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th>Destination</th>
          <th>Rate</th>
          <th>Currency</th>
          <th>Start Date</th>
        </tr>
      );
    }
  };
  const renderTableRow = (detail, idx) => {
    if (showDetails) {
      return (
        <tr key={idx}>
          <td><Badge bg="secondary">{detail.prefix}</Badge></td>
          <td>{detail.description}</td>
          <td className="rate-cell">{detail.voice_rate}</td>
          <td>{detail.currency_identifier}</td>
          <td>{new Date(detail.st_date).toLocaleDateString()}</td>
          <td>{detail.bill_rule_name}</td>
        </tr>
      );
    } else {
      return (
        <tr key={idx}>
          <td>{detail.description}</td>
          <td className="rate-cell">{detail.voice_rate}</td>
          <td>{detail.currency_identifier}</td>
          <td>{new Date(detail.st_date).toLocaleDateString()}</td>
        </tr>
      );
    }
  };
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading gateways...</p>
      </Container>
    );
  }
  return (
    <Container className="gateways-container mt-4">
      <div className="gateways-header mb-4">
        <div className="d-flex align-items-center">
          <Network size={32} className="me-3 text-primary" />
          <h2 className="mb-0">Gateways Management</h2>
        </div>
      </div>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      <Tabs defaultActiveKey="voice" className="mb-4 gateway-tabs">
        <Tab eventKey="voice" title={<span><Network size={16} className="me-2" />Voice Gateways</span>}>
          {voicePermissions.vie === 0 ? (
            <Alert variant="warning">You do not have permission to view Voice gateways</Alert>
          ) : (
            <>
              {voicePermissions.ins === 1 && (
                <div className="mb-3">
                  <Button variant="primary" onClick={handleAddVoice}>
                    <Plus size={16} className="me-2" />
                    Add Voice Gateway
                  </Button>
                </div>
              )}
              <Table hover responsive className="gateways-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>IP Address</th>
                    <th>Protocol</th>
                    <th>Tech Prefix</th>
                    <th>Tech Prefix Out</th>
                    <th>Tariff Sale</th>
                    <th>Tariff Term</th>
                    <th>Capacity</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {voiceGateways.length === 0 ? (
                    <tr><td colSpan="11" className="text-center text-muted py-4">No voice gateways found</td></tr>
                  ) : (
                    voiceGateways.map((gw) => (
                      <tr key={gw.id_route}>
                        <td>{gw.description}</td>
                        <td><Badge bg="info">{gw.ip_number}</Badge></td>
                        <td>{gw.protocol_name} : {gw.protocol_port}</td>
                        <td>{gw.tech_prefix}</td>
                        <td>{gw.tech_prefix_out}</td>
                        <td>
                          {gw.id_tariff > 0 ? (
                            <Button size="sm" variant="link" onClick={() => viewTariff(gw.id_tariff, 'sale', 'voice')}>
                              {gw.tariff_sale_name || `Tariff #${gw.id_tariff}`}
                            </Button>
                          ) : '-'}
                        </td>
                        <td>
                          {gw.id_tariff_term > 0 ? (
                            <Button size="sm" variant="link" onClick={() => viewTariff(gw.id_tariff_term, 'term', 'voice')}>
                              {gw.tariff_term_name || `Tariff #${gw.id_tariff_term}`}
                            </Button>
                          ) : '-'}
                        </td>
                        <td>{gw.capacity || '-'} / {gw.capacity_term || '-'}</td>
                        <td>{gw.active === 1 ? <Badge bg="success">Active</Badge> : <Badge bg="warning">Pending</Badge>}</td>
                        <td>{new Date(gw.add_date).toLocaleDateString()}</td>
                        <td>
                          {voicePermissions.upd === 1 && (
                            <Button size="sm" variant="warning" onClick={() => handleEditVoice(gw)} className="me-1">
                              <Edit size={14} />
                            </Button>
                          )}
                          {voicePermissions.del === 1 && (
                            <Button size="sm" variant="danger" onClick={() => handleDeleteVoice(gw.id_route)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                          {voicePermissions.upd === 0 && voicePermissions.del === 0 && <span className="text-muted">View only</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Tab>
        {clientType === 7 && (
          <Tab eventKey="sms" title={<span><MessageSquare size={16} className="me-2" />SMS Gateways</span>}>
            {smsPermissions.vie === 0 ? (
              <Alert variant="warning">You do not have permission to view SMS gateways</Alert>
            ) : (
              <>
                {smsPermissions.ins === 1 && (
                  <div className="mb-3">
                    <Button variant="success" onClick={handleAddSms}>
                      <Plus size={16} className="me-2" />
                      Add SMS Gateway
                    </Button>
                  </div>
                )}
                <Table hover responsive className="gateways-table sms-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>IP Address</th>
                      <th>Port</th>
                      <th>Protocol</th>
                      <th>System ID</th>
                      <th>System ID Term</th>
                      <th>Tariff Sale</th>
                      <th>Tariff Term</th>
                      <th>Sessions IN/OUT</th>
                      <th>Status</th>
                      <th>Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsGateways.length === 0 ? (
                      <tr><td colSpan="12" className="text-center text-muted py-4">No SMS gateways found</td></tr>
                    ) : (
                      smsGateways.map((gw) => (
                        <tr key={gw.id}>
                          <td>{gw.gw_name}</td>
                          <td><Badge bg="info">{gw.gw_ip}</Badge></td>
                          <td>{gw.gw_port}</td>
                          <td>{gw.protocol_name}</td>
                          <td>{gw.systemid}</td>
                          <td>{gw.systemid_term}</td>
                          <td>
                            {gw.tariff_id > 0 ? (
                              <Button size="sm" variant="link" onClick={() => viewTariff(gw.tariff_id, 'sale', 'sms')}>
                                {gw.tariff_sale_name || `Tariff #${gw.tariff_id}`}
                              </Button>
                            ) : '-'}
                          </td>
                          <td>
                            {gw.tariff_term_id > 0 ? (
                              <Button size="sm" variant="link" onClick={() => viewTariff(gw.tariff_term_id, 'term', 'sms')}>
                                {gw.tariff_term_name || `Tariff #${gw.tariff_term_id}`}
                              </Button>
                            ) : '-'}
                          </td>
                          <td>{gw.tcp_sess_max_in} / {gw.tcp_sess_num_out}</td>
                          <td>{gw.status === 1 ? <Badge bg="success">Active</Badge> : <Badge bg="warning">Pending</Badge>}</td>
                          <td>{new Date(gw.add_date).toLocaleDateString()}</td>
                          <td>
                            {smsPermissions.upd === 1 && (
                              <Button size="sm" variant="warning" onClick={() => handleEditSms(gw)} className="me-1">
                                <Edit size={14} />
                              </Button>
                            )}
                            {smsPermissions.del === 1 && (
                              <Button size="sm" variant="danger" onClick={() => handleDeleteSms(gw.id)}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                            {smsPermissions.upd === 0 && smsPermissions.del === 0 && <span className="text-muted">View only</span>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </Tab>
        )}
      </Tabs>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'Edit' : 'Add'} {modalType === 'voice' ? 'Voice' : 'SMS'} Gateway</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalType === 'voice' ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.description} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, description: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>IP Address *</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.ip_number} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, ip_number: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Protocol</Form.Label>
                <Form.Select value={currentVoiceGateway.id_prot} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, id_prot: e.target.value })}>
                  {protocols.map((prot) => <option key={prot.id} value={prot.id}>{prot.descr} : {prot.port}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tech Prefix</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.tech_prefix} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, tech_prefix: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tech Prefix Out</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.tech_prefix_out} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, tech_prefix_out: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Capacity</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.capacity} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, capacity: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Capacity Term</Form.Label>
                <Form.Control type="text" value={currentVoiceGateway.capacity_term} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, capacity_term: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Max Connections/Min</Form.Label>
                <Form.Control type="number" value={currentVoiceGateway.max_conn_min} onChange={(e) => setCurrentVoiceGateway({ ...currentVoiceGateway, max_conn_min: e.target.value })} />
              </Form.Group>
            </Form>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Gateway Name *</Form.Label>
                <Form.Control type="text" value={currentSmsGateway.gw_name} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, gw_name: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>IP Address *</Form.Label>
                <Form.Control type="text" value={currentSmsGateway.gw_ip} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, gw_ip: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Port *</Form.Label>
                <Form.Control type="number" value={currentSmsGateway.gw_port} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, gw_port: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Protocol</Form.Label>
                <Form.Select value={currentSmsGateway.protocol_id} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, protocol_id: e.target.value })}>
                  {smsProtocols.map((prot) => <option key={prot.id} value={prot.id}>{prot.descr}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>System ID</Form.Label>
                <Form.Control type="text" value={currentSmsGateway.systemid} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, systemid: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>System ID Term</Form.Label>
                <Form.Control type="text" value={currentSmsGateway.systemid_term} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, systemid_term: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>TLS Term ID</Form.Label>
                <Form.Control type="number" value={currentSmsGateway.tls_term_id} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, tls_term_id: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>SSL Port</Form.Label>
                <Form.Control type="number" value={currentSmsGateway.gw_port_ssl || ''} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, gw_port_ssl: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Max Sessions IN</Form.Label>
                <Form.Control type="number" value={currentSmsGateway.tcp_sess_max_in} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, tcp_sess_max_in: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Sessions OUT</Form.Label>
                <Form.Control type="number" value={currentSmsGateway.tcp_sess_num_out} onChange={(e) => setCurrentSmsGateway({ ...currentSmsGateway, tcp_sess_num_out: e.target.value })} />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={modalType === 'voice' ? handleSaveVoice : handleSaveSms}>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showTariffModal} onHide={handleCloseTariffModal} size="xl" centered className="tariff-modal">
        <Modal.Header className={selectedTariff.service === 'voice' ? 'voice-modal-header' : 'sms-modal-header'}>
          <div className="d-flex align-items-center w-100 justify-content-between">
            <div className="d-flex align-items-center">
              {selectedTariff.service === 'voice' ? <Network size={24} className="me-2" /> : <MessageSquare size={24} className="me-2" />}
              <Modal.Title>Tariff Details - {selectedTariff.type}</Modal.Title>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge bg={selectedTariff.service === 'voice' ? 'primary' : 'success'} className="text-uppercase px-3 py-2">
                {selectedTariff.service}
              </Badge>
              <Button variant="link" onClick={handleCloseTariffModal} className="close-modal-btn p-0 text-white">
                ✕
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <div className="search-box">
              <Search size={20} className="search-icon" />
              <Form.Control 
                type="text" 
                placeholder="Search by prefix or country code..." 
                value={tariffSearch} 
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
              <Button variant={selectedTariff.service === 'voice' ? 'primary' : 'success'} onClick={handleSearch} className="search-btn">
                Search
              </Button>
            </div>
          </Form.Group>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="alphabet-filter">
              <button className={`letter-btn off-btn ${selectedLetter === '' ? 'active' : ''}`} onClick={handleOffClick}>OFF</button>
              {alphabet.map(letter => (
                <button key={letter} className={`letter-btn ${selectedLetter === letter ? 'active' : ''}`} onClick={() => handleLetterClick(letter)}>
                  {letter}
                </button>
              ))}
              
            </div>
            <Form.Check 
              type="checkbox"
              id="show-details-check"
              label="Show more details"
              checked={showDetails}
              onChange={(e) => handleShowDetailsChange(e.target.checked)}
              className="ms-3"
            />
          </div>
          {detailsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant={selectedTariff.service === 'voice' ? 'primary' : 'success'} />
              <p className="mt-3">Loading rates...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped hover className="rates-table">
                  <thead>{getTableColumns()}</thead>
                  <tbody>
                    {tariffDetails.length > 0 ? (
                      tariffDetails.map((detail, idx) => renderTableRow(detail, idx))
                    ) : (
                      <tr>
                        <td colSpan={showDetails ? "6" : "4"} className="text-center text-muted py-4">
                          No rates found. Try adjusting your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {renderPagination()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTariffModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default Gateways;